import {
  formatearMonto,
  formatearHora,
  formatearFechaHora,
  formatearNumeroComprobante,
  ETIQUETAS_METODO,
  ETIQUETAS_MOTIVO,
} from '../utils/formato';
import { IconExito, IconImprimir } from './icons';

/**
 * Comprobante de cierre de caja: mismo componente para el cierre recién hecho
 * (Caja.jsx) y para cualquier cierre pasado (Historial.jsx). Pensado para
 * imprimirse — ver ".reporte-imprimible" y "@media print" en caja.css.
 *
 * Si recibe `cobros`, muestra además todos los movimientos del turno (los anulados
 * marcados como tales, con el original intacto). `onVerComprobante` agrega el botón
 * para abrir el comprobante individual de cada cobro.
 */
function ReporteCierre({
  sesionCaja,
  reporteCierre,
  operador,
  cobros,
  onVerComprobante,
  onVolver,
  textoVolver = 'Volver',
}) {
  const totales = reporteCierre.totales_por_metodo;
  const totalCantidad = totales.reduce((acc, t) => acc + Number(t.cantidad), 0);
  const totalGeneral = totales.reduce((acc, t) => acc + Number(t.total), 0);
  const anulados = reporteCierre.anulados || { cantidad: 0, total: 0 };

  return (
    <section className="tarjeta reporte-imprimible">
      <div className="reporte-encabezado">
        <img src="/logo-betech.svg" alt="Betech" className="reporte-logo" />
        <div>
          <h2>Cierre de caja</h2>
          <p className="reporte-subtitulo">Comprobante de arqueo — no editable</p>
        </div>
      </div>

      <div className="resumen-grilla">
        <div>
          <span className="etiqueta">Municipio</span>
          <span>{sesionCaja.municipio_id}</span>
        </div>
        <div>
          <span className="etiqueta">Operador</span>
          <span>{operador?.usuario || sesionCaja.operador_id}</span>
        </div>
        <div>
          <span className="etiqueta">Apertura</span>
          <span>{formatearFechaHora(sesionCaja.fecha_apertura)}</span>
        </div>
        <div>
          <span className="etiqueta">Cierre</span>
          <span>{formatearFechaHora(sesionCaja.fecha_cierre)}</span>
        </div>
      </div>

      <div className="resumen-grilla">
        <div>
          <span className="etiqueta">Monto inicial</span>
          <span>{formatearMonto(sesionCaja.monto_inicial)}</span>
        </div>
        <div>
          <span className="etiqueta">Efectivo esperado</span>
          <span>{formatearMonto(reporteCierre.esperado_efectivo)}</span>
        </div>
        <div>
          <span className="etiqueta">Efectivo contado</span>
          <span>{formatearMonto(sesionCaja.monto_final_declarado)}</span>
        </div>
        <div>
          <span className="etiqueta">Diferencia de arqueo</span>
          <span className={Number(reporteCierre.diferencia_arqueo) === 0 ? '' : 'diferencia-destacada'}>
            {formatearMonto(reporteCierre.diferencia_arqueo)}
          </span>
        </div>
      </div>

      <h3>Total cobrado por método de pago</h3>
      {totales.length === 0 ? (
        <p>No hubo cobros vigentes en este turno.</p>
      ) : (
        <div className="tabla-cobros-wrapper">
          <table className="tabla-cobros">
            <thead>
              <tr>
                <th>Método</th>
                <th>Cantidad</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {totales.map((t) => (
                <tr key={t.metodo_pago}>
                  <td>{ETIQUETAS_METODO[t.metodo_pago] || t.metodo_pago}</td>
                  <td>{t.cantidad}</td>
                  <td>{formatearMonto(t.total)}</td>
                </tr>
              ))}
              <tr>
                <td>
                  <strong>Total</strong>
                </td>
                <td>
                  <strong>{totalCantidad}</strong>
                </td>
                <td>
                  <strong>{formatearMonto(totalGeneral)}</strong>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {anulados.cantidad > 0 && (
        <p className="nota-anulados">
          Cobros anulados en este turno: {anulados.cantidad} por {formatearMonto(anulados.total)}. No están incluidos en los
          totales de arriba.
        </p>
      )}

      {cobros && cobros.length > 0 && (
        <>
          <h3>Movimientos del turno</h3>
          <div className="tabla-cobros-wrapper">
            <table className="tabla-cobros tabla-movimientos">
              <thead>
                <tr>
                  <th>N°</th>
                  <th>Hora</th>
                  <th>Contribuyente</th>
                  <th>Trámite</th>
                  <th>Método</th>
                  <th>Monto</th>
                  <th>Estado</th>
                  {onVerComprobante && <th className="no-imprimir"></th>}
                </tr>
              </thead>
              <tbody>
                {cobros.map((c) => (
                  <tr key={c.id} className={c.anulacion ? 'fila-anulada' : ''}>
                    <td data-label="N°">#{formatearNumeroComprobante(c.numero_comprobante)}</td>
                    <td data-label="Hora">{formatearHora(c.fecha_hora)}</td>
                    <td data-label="Contribuyente" className="celda-flexible">{c.contribuyente_nombre}</td>
                    <td data-label="Trámite">{ETIQUETAS_MOTIVO[c.motivo] || c.motivo}</td>
                    <td data-label="Método" className="celda-flexible">{ETIQUETAS_METODO[c.metodo_pago] || c.metodo_pago}</td>
                    <td data-label="Monto" className="celda-monto">{formatearMonto(c.monto)}</td>
                    <td data-label="Estado">
                      {c.anulacion ? <span className="badge-anulado">Anulado</span> : <span className="badge-vigente">Vigente</span>}
                    </td>
                    {onVerComprobante && (
                      <td className="no-imprimir celda-acciones">
                        <button type="button" className="boton boton-chico boton-secundario" onClick={() => onVerComprobante(c)}>
                          Comprobante
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <p className="reporte-nota">
        <IconExito size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
        Este cierre quedó registrado y no se puede modificar.
      </p>

      <div className="reporte-acciones no-imprimir">
        <button type="button" className="boton boton-secundario boton-con-icono" onClick={() => window.print()}>
          <IconImprimir size={16} />
          Imprimir
        </button>
        {onVolver && (
          <button type="button" className="boton boton-primario" onClick={onVolver}>
            {textoVolver}
          </button>
        )}
      </div>
    </section>
  );
}

export default ReporteCierre;
